import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Swords } from 'lucide-react';

import { api } from '@/lib/api';
import { useSocketEvent, useSocketStatus } from '@/lib/useSocket';

interface OngoingGame {
  id: string;
  color: 'white' | 'black';
  opponent: string;
  myTurn: boolean;
  speed: string;
  moves: number;
}

/** Cada cuánto se repasa la lista, solo con la pestaña a la vista. */
const REFRESH_MS = 5_000;

/**
 * Partidas a las que ya se saltó una vez. Si el jugador sale de una a
 * propósito, no se le vuelve a arrastrar a ella.
 */
const followed = new Set<string>();

/**
 * Partidas en tiempo real en marcha: lleva al tablero en cuanto empieza una
 * y deja un aviso bajo la cabecera para volver a las que sigan abiertas.
 *
 * El salto llega normalmente por WebSocket (`lobby:matched`, que se manda a
 * los dos jugadores al aceptarse un reto o emparejarse en el lobby). Si ese
 * aviso se pierde —una reconexión justo en ese momento—, el repaso periódico
 * encuentra la partida recién empezada y salta igual.
 */
export function OngoingGamesBanner() {
  const [games, setGames] = useState<OngoingGame[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const status = useSocketStatus();

  const follow = useCallback(
    (gameId: string) => {
      if (followed.has(gameId)) return;
      followed.add(gameId);
      const target = `/partida/${gameId}`;
      if (window.location.pathname !== target) navigate(target);
    },
    [navigate],
  );

  const load = useCallback(() => {
    api
      .get<{ items: OngoingGame[] }>('/games/en-curso')
      .then((data) => {
        const active = (data.items || []).filter((g) => !g.id.startsWith('tv-'));
        setGames(active);
        // Una partida que aún no ha arrancado de verdad es que acaba de
        // empezar: allí es donde hay que estar.
        for (const game of active) {
          if (game.moves < 2) follow(game.id);
        }
      })
      .catch(() => undefined);
  }, [follow]);

  useEffect(load, [load, location.pathname, status]);
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useSocketEvent<{ gameId?: string }>('lobby:matched', (data) => {
    if (data?.gameId && !data.gameId.startsWith('tv-')) follow(data.gameId);
    load();
  });
  useSocketEvent<{ gameId?: string }>('tournament:yourGame', (data) => {
    if (data?.gameId && !data.gameId.startsWith('tv-')) follow(data.gameId);
    load();
  });
  useSocketEvent<{ gameId?: string | null }>('challenge:accepted', (data) => {
    if (data?.gameId && !data.gameId.startsWith('tv-')) follow(data.gameId);
    load();
  });

  const visible = games.filter(
    (game) => !game.id.startsWith('tv-') && location.pathname !== `/partida/${game.id}`,
  );
  if (visible.length === 0) return null;

  return (
    <div className="border-b border-border bg-brand/10">
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-4 gap-y-1.5 px-3 py-2 sm:px-4">
        {visible.map((game) => (
          <div key={game.id} className="flex min-w-0 items-center gap-2 text-sm">
            <Swords size={16} className="shrink-0 text-brand-strong" />
            <span className="truncate">
              Partida en juego contra <strong className="font-medium">{game.opponent}</strong>
              {game.myTurn ? <span className="text-brand-strong"> · te toca</span> : null}
            </span>
            <Link to={`/partida/${game.id}`} className="btn btn-primary px-3 py-1 text-sm">
              Entrar
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}
